import React, { useCallback, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MultiSelect } from 'primereact/multiselect';
import { InputSwitch } from 'primereact/inputswitch';
import { RadioButton } from 'primereact/radiobutton';

import { useFirmUserList } from 'hooks/useFirmUserList';
import { formatUserDropdown } from 'modules/automation/utils/formatUserDropdown';
import { AssignToItemTemplate } from 'modules/automation/WorkflowBuilder/components';
import { automationModules } from 'constants/automation';
import { toTitleCase } from 'utils/utils';

const EventAttendees = ({ index }) => {
  const { control, watch, resetField } = useFormContext();
  const { userList, isFirmUserLoading } = useFirmUserList(true, false, true, true, '');
  const trigger = watch('trigger') || {};

  const isForLeadAutomation =
    watch('is_lead_automation') || (trigger?.module === automationModules.LEADS && trigger?.code !== 'TRIG_LEAD_CONV_CASE');
  const selectedAttendees = watch(`actions[${index}].assignTo`) || [];
  const assigneeReminder = watch(`actions[${index}].assignee_reminder`) || [];

  const getNameFromid = useCallback(
    (val) => {
      if (val.res_type === 'user-group') {
        const user = userList?.find((user) => user.user_group_id === val.value);
        return user?.group_name;
      }
      if (val.res_type === 'user') {
        const user = userList?.find((user) => user.firm_user_id === val.value);
        return user ? user.first_name : '';
      }
      return toTitleCase(val?.value);
    },
    [userList]
  );

  useEffect(() => {
    const synced = selectedAttendees.map((att) => {
      const existing = assigneeReminder.find(
        (r) => (att.res_type === 'user' && r.firm_user_id === att.value) || (att.res_type !== 'user' && r.role === att.value)
      );
      return (
        existing || {
          [att.res_type === 'user' ? 'firm_user_id' : 'role']: att.value,
          is_attendance_required: '',
          send_reminder: false,
        }
      );
    });

    if (JSON.stringify(synced) !== JSON.stringify(assigneeReminder)) {
      resetField(`actions[${index}].assignee_reminder`, { defaultValue: synced, keepTouched: true });
    }
  }, [selectedAttendees]);

  return (
    <div className="d-flex flex-column">
      <Controller
        name={`actions[${index}].assignTo`}
        control={control}
        render={({ field: { name, value, onChange } }) => (
          <MultiSelect
            name={name}
            value={userList?.length && value?.length > 0 ? value : []}
            options={
              userList?.length > 0
                ? formatUserDropdown({
                    users: userList,
                    hideInactive: true,
                    includeContacts: false,
                    includesUserGroups: true,
                    includesTeamMembers: true,
                    includePrimarySecondary: true,
                    isLead: isForLeadAutomation,
                    includeSelectedInactiveUser: true,
                    includeSelectedInactiveUserGroup: true,
                    selectedItems: value || [],
                  })
                : []
            }
            onChange={(e) => onChange(e.value)}
            isFilter={true}
            placeholder="Select Attendees"
            className="w-100 input-shadow"
            display="chip"
            optionLabel="label"
            optionGroupLabel="groupLabel"
            optionGroupChildren="items"
            optionValue={(v) => ({ res_type: v?.res_type, value: v?.value })}
            itemTemplate={AssignToItemTemplate}
            dropdownIcon={isFirmUserLoading ? 'pi pi-spin pi-spinner' : undefined}
          />
        )}
      />
      {isForLeadAutomation && (
        <div className="d-flex mt-2 files-note">
          <i className="pi pi-info-circle me-1"></i>
          If 'On Behalf Of' contact is not available, 'Client' will be used automatically, even if not selected.
        </div>
      )}
      {selectedAttendees.length > 0 && (
        <table className="table attendees-table mt-2">
          <thead>
            <tr>
              <th>Attendees</th>
              <th className="text-center">Required</th>
              <th className="text-center">Optional</th>
              <th className="text-center">Send Reminder</th>
            </tr>
          </thead>
          <tbody>
            {selectedAttendees.map((att, id) => {
              const reminderField = `actions[${index}].assignee_reminder[${id}]`;
              return (
                <tr key={att.value}>
                  <td>{getNameFromid(att)}</td>
                  <Controller
                    name={`${reminderField}.is_attendance_required`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <td className="text-center">
                          <RadioButton value="required" checked={field.value === 'required'} onChange={(e) => field.onChange(e.value)} />
                        </td>
                        <td className="text-center">
                          <RadioButton value="optional" checked={field.value === 'optional'} onChange={(e) => field.onChange(e.value)} />
                        </td>
                      </>
                    )}
                  />
                  <Controller
                    name={`${reminderField}.send_reminder`}
                    control={control}
                    render={({ field }) => (
                      <td className="text-center">
                        <InputSwitch checked={field.value} onChange={(e) => field.onChange(e.value)} />
                      </td>
                    )}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default EventAttendees;
